import { useNavigate } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { formatClock } from '@bookingdei/i18n/format-date';
import { useLanguage } from '../../shell/language-context';
import { NavIcon } from '../../shell/nav-icon';
import { branchLabel, brandLabel, businessDay, businessName } from './home-model';
import type { Business, MerchantBooking } from '../../mock/seeds/schema-types';
import { useHomeMoney } from './use-home-money';

// Cards shown on Home before "See all" (design stackItems.slice(0, 3)).
const PREVIEW = 3;

// "My Business" (M:593-653, bizPreview M:12439): a gradient card per business with
// today's bookings, expected revenue (today's booking totals), a "N to review / All
// clear" chip and the next slot; then the Add-business card while the plan allows
// another business (entitlement `businesses`, DB-53a) — or an Upgrade pill at the cap.
export function HomeBusinessCards({
  businesses,
  today,
  cap,
  onPick,
  serviceName,
}: {
  businesses: readonly Business[];
  today: readonly MerchantBooking[];
  /** `businesses` entitlement; null/undefined = unlimited. */
  cap: number | null | undefined;
  onPick: (businessId: string) => void;
  serviceName: (b: MerchantBooking) => string;
}) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const money = useHomeMoney();
  const canAdd = cap === null || cap === undefined || businesses.length < cap;
  const preview = businesses.slice(0, PREVIEW);

  return (
    <section className="hm-card hm-biz" aria-labelledby="hm-biz-title">
      <div className="hm-card__head">
        <h3 id="hm-biz-title" className="hm-card__title">
          {t('dc70.nav.business')}
        </h3>
        <span className="hm-count hm-nowrap">
          {formatMessage(lang, 'dc71.biz.chip', { n: businesses.length, total: today.length })}
        </span>
        <button type="button" className="hm-link hm-push" onClick={() => navigate('/business')}>
          {businesses.length > PREVIEW ? formatMessage(lang, 'dc71.biz.see_all_n', { n: businesses.length }) : t('dc71.biz.see_all')} →
        </button>
      </div>
      <div className="hm-biz__list">
        {preview.map((b, i) => {
          const day = businessDay(today, b.id);
          const name = businessName(b, lang);
          return (
            <button key={b.id} type="button" className={`hm-bcard hm-bcard--${(i % 3) + 1}`} data-business={b.id} onClick={() => onPick(b.id)}>
              <span className="hm-bcard__head">
                <span className="hm-bcard__names">
                  <span className="hm-bcard__name">{branchLabel(name)}</span>
                  <span className="hm-bcard__sub">{brandLabel(name)}</span>
                </span>
                <span className="hm-bcard__chip">
                  <span className={day.toReview ? 'hm-bcard__dot is-review' : 'hm-bcard__dot'} />
                  {day.toReview ? formatMessage(lang, 'dc71.biz.to_review', { n: day.toReview }) : t('dc71.biz.all_clear')}
                </span>
              </span>
              <span className="hm-bcard__figs">
                <span className="hm-bcard__fig">
                  <span className="hm-bcard__num">{formatNumber(lang, day.count)}</span>
                  <span className="hm-bcard__cap">{t('dc71.biz.count_cap')}</span>
                </span>
                <span className="hm-bcard__fig">
                  <span className="hm-bcard__num">{money(day.revenueMinor)}</span>
                  <span className="hm-bcard__cap">{t('dc71.biz.revenue_cap')}</span>
                </span>
              </span>
              <span className="hm-bcard__next">
                <NavIcon name="clock" size={14} strokeWidth={1.9} />
                <span className="hm-bcard__next-text">
                  {day.next
                    ? formatMessage(lang, 'dc71.biz.next', {
                        time: day.next.starts_at
                          ? formatClock(lang, new Date(day.next.starts_at).getHours(), new Date(day.next.starts_at).getMinutes(), { alwaysMinutes: true })
                          : '—',
                        service: serviceName(day.next),
                      })
                    : t('dc71.biz.nothing_left')}
                </span>
              </span>
            </button>
          );
        })}
        {canAdd && businesses.length < PREVIEW && (
          <button type="button" className="hm-addbiz" onClick={() => navigate('/business?new=1')}>
            <span className="hm-addbiz__plus">
              <NavIcon name="plus" size={20} strokeWidth={2.2} />
            </span>
            <span className="hm-addbiz__text">
              <span className="hm-addbiz__title">{t('dc71.biz.add')}</span>
              <span className="hm-addbiz__help">{t('dc71.biz.add_help')}</span>
            </span>
          </button>
        )}
      </div>
      {(!canAdd || businesses.length >= PREVIEW) && (
        <div className="hm-biz__foot">
          {canAdd ? (
            <button type="button" className="hm-btn hm-btn--wide" onClick={() => navigate('/business')}>
              <NavIcon name="plus" size={18} strokeWidth={2.2} />
              <span>{t('dc71.biz.add')}</span>
            </button>
          ) : (
            <button type="button" className="hm-pill hm-pill--wide" onClick={() => navigate('/plan')}>
              {t('dc71.biz.at_cap')}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
